import type { EChartsOption } from "echarts";

// Doppler color palette
const CHART_COLORS = [
	"#008782", // primary teal
	"#2563EB", // blue
	"#D97706", // amber
	"#DC2626", // red
	"#7C3AED", // violet
	"#059669", // emerald
	"#DB2777", // pink
	"#EA580C", // orange
	"#4F46E5", // indigo
	"#0D9488", // teal-alt
];

type ValueFormat = "currency_usd" | "percentage" | "ratio" | "bps" | "number" | "compact" | undefined;

const formatValue = (value: number, format: ValueFormat): string => {
	if (value == null) return "\u2014";
	switch (format) {
		case "currency_usd":
			return value >= 1_000_000
				? `$${(value / 1_000_000).toFixed(1)}M`
				: value >= 1_000
					? `$${(value / 1_000).toFixed(1)}K`
					: `$${value.toLocaleString()}`;
		case "percentage":
			return `${value.toFixed(2)}%`;
		case "ratio":
			return `${value.toFixed(2)}x`;
		case "bps":
			return `${value.toFixed(0)} bps`;
		case "compact":
			return value >= 1_000_000
				? `${(value / 1_000_000).toFixed(1)}M`
				: value >= 1_000
					? `${(value / 1_000).toFixed(1)}K`
					: value.toLocaleString();
		case "number":
		default:
			return value.toLocaleString();
	}
};

interface CartesianSeries {
	name: string;
	values: (number | null)[];
	value_format?: ValueFormat;
	style?: string;
}

interface PieSegment {
	name: string;
	value: number;
	value_format?: ValueFormat;
}

interface ScatterSeries {
	name: string;
	points: [number, number][];
	value_format?: ValueFormat;
}

interface CartesianChartData {
	labels: string[];
	series: CartesianSeries[];
	value_format?: ValueFormat;
}

interface PieChartData {
	segments: PieSegment[];
	value_format?: ValueFormat;
}

interface ScatterChartData {
	series: ScatterSeries[];
	value_format?: ValueFormat;
}

export const buildEChartsOption = (
	chartType: string,
	chartData: Record<string, unknown>,
	title?: string,
): EChartsOption => {
	const baseOption: EChartsOption = {
		color: CHART_COLORS,
		title: title ? { text: title, left: "center", textStyle: { fontSize: 14, fontWeight: 600 } } : undefined,
		animation: true,
		animationDuration: 400,
	};

	if (chartType === "pie") {
		return buildPieOption(baseOption, chartData as unknown as PieChartData);
	}
	if (chartType === "scatter") {
		return buildScatterOption(baseOption, chartData as unknown as ScatterChartData);
	}
	// line, bar, area -> cartesian
	return buildCartesianOption(baseOption, chartType, chartData as unknown as CartesianChartData);
};

const buildCartesianOption = (
	base: EChartsOption,
	chartType: string,
	data: CartesianChartData,
): EChartsOption => {
	const globalFormat = data.value_format;
	return {
		...base,
		tooltip: {
			trigger: "axis",
			valueFormatter: (value: unknown) => formatValue(value as number, globalFormat),
		},
		legend: { bottom: 0, type: "scroll" },
		grid: { left: "3%", right: "4%", bottom: "15%", containLabel: true },
		xAxis: { type: "category", data: data.labels ?? [], axisLabel: { rotate: data.labels?.length > 8 ? 45 : 0 } },
		yAxis: {
			type: "value",
			axisLabel: { formatter: (v: number) => formatValue(v, globalFormat) },
		},
		series: (data.series ?? []).map((s) => {
			const fmt = s.value_format ?? globalFormat;
			const isArea = chartType === "area" || s.style === "area";
			return {
				name: s.name,
				type: chartType === "bar" ? "bar" : "line",
				data: s.values,
				areaStyle: isArea ? {} : undefined,
				smooth: chartType !== "bar",
				tooltip: { valueFormatter: (value: unknown) => formatValue(value as number, fmt) },
			};
		}),
	};
};

const buildPieOption = (
	base: EChartsOption,
	data: PieChartData,
): EChartsOption => {
	const globalFormat = data.value_format;
	return {
		...base,
		tooltip: {
			trigger: "item",
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			formatter: (params: any) => {
				const val = formatValue(params.value ?? 0, globalFormat);
				return `${params.name}: ${val} (${(params.percent ?? 0).toFixed(1)}%)`;
			},
		},
		legend: { bottom: 0, type: "scroll" },
		series: [
			{
				type: "pie",
				radius: ["40%", "70%"],
				avoidLabelOverlap: true,
				itemStyle: { borderRadius: 4, borderColor: "#fff", borderWidth: 2 },
				label: { show: false },
				emphasis: { label: { show: true, fontSize: 14, fontWeight: "bold" } },
				data: (data.segments ?? []).map((seg) => ({
					name: seg.name,
					value: seg.value,
				})),
			},
		],
	};
};

const buildScatterOption = (
	base: EChartsOption,
	data: ScatterChartData,
): EChartsOption => {
	const globalFormat = data.value_format;
	return {
		...base,
		tooltip: {
			trigger: "item",
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			formatter: (params: any) => {
				const [x, y] = params.value ?? [0, 0];
				return `${params.seriesName}: (${formatValue(x, globalFormat)}, ${formatValue(y, globalFormat)})`;
			},
		},
		legend: { bottom: 0, type: "scroll" },
		grid: { left: "3%", right: "4%", bottom: "15%", containLabel: true },
		xAxis: {
			type: "value",
			axisLabel: { formatter: (v: number) => formatValue(v, globalFormat) },
		},
		yAxis: {
			type: "value",
			axisLabel: { formatter: (v: number) => formatValue(v, globalFormat) },
		},
		series: (data.series ?? []).map((s) => ({
			name: s.name,
			type: "scatter",
			data: s.points,
			symbolSize: 8,
		})),
	};
};
